import { retrieveRouteComponentsFromPath } from "./retrieve-route-components-from-path";
import { RouteElement } from "./RouteElement";
import type { RouteConfig, ShouldShowContext } from "./types";
import { Navigate, Route } from "react-router-dom";

export type RouteElementArgs = RouteConfig & {
  path: string;
};

export interface GetRouteComponentOptions {
  shouldShowContext: ShouldShowContext | null;
}

/** This looks like a component, but must be a function since react-router-dom requires Router children to be their exact component */
export const getRouteComponent = ({ path, ...config }: RouteElementArgs, options: GetRouteComponentOptions) => {
  const subRoutes = config.subRoutes ? Object.entries(config.subRoutes) : [];
  const { pageComponent, useDefaultTitle, indexRedirect } = retrieveRouteComponentsFromPath(path) ?? {};

  if (config.shouldShow !== undefined) {
    if (typeof config.shouldShow === "function") {
      if (options.shouldShowContext && !config.shouldShow(options.shouldShowContext)) return null;
    } else if (!config.shouldShow) {
      return null;
    }
  }

  if (config.modelForAccess && options.shouldShowContext) {
    if (options.shouldShowContext.session.userInfo.access[config.modelForAccess]?.showInSidebar === false) return null;
    if (!options.shouldShowContext.session.userInfo.access[config.modelForAccess]?.read) return null;
  }

  const indexElement =
    typeof indexRedirect === "string" ? (
      <Navigate replace to={indexRedirect} />
    ) : indexRedirect ? (
      <RouteElement
        isProtected={config.isProtected}
        hideMainLayout={!!config.hideMainLayout}
        useDefaultTitle={useDefaultTitle}
      >
        {indexRedirect}
      </RouteElement>
    ) : null;

  return (
    <Route
      key={path}
      path={path}
      element={
        pageComponent ? (
          <RouteElement
            isProtected={config.isProtected}
            hideMainLayout={!!config.hideMainLayout}
            useDefaultTitle={useDefaultTitle}
          >
            {pageComponent}
          </RouteElement>
        ) : undefined
      }
    >
      {indexElement && <Route index element={indexElement} />}
      {subRoutes.map(([subRoutePath, subConfig]) =>
        getRouteComponent(
          {
            path: `${path}${subRoutePath}`,
            ...subConfig,
          },
          options
        )
      )}
    </Route>
  );
};
